import { CompanyPartner } from 'src/features/company/api';
import api from '../../shared/api/axios';

import type { Project, Member, CompanyUserLite, ProjectSetting, ProjectFxSetting } from './types';
import type { Role } from '@features/admin/api';

export async function listMyProjects(): Promise<{ ok: boolean; data: Project[] }> {
    const { data } = await api.get('/projects');
    return data;
}

export async function createProject(params: { name: string; code?: string }) {
    const { data } = await api.post('/projects', params);
    return data;
}

export async function getProject(id: string): Promise<{ ok: boolean; data: Project }> {
    const { data } = await api.get(`/projects/${id}`);
    return data;
}

// DRAFT 프로젝트 하드 삭제
export async function deleteProject(id: string) {
    const { data } = await api.delete(`/projects/${id}`);
    return data;
}

// ACTIVE → CLOSED
export async function closeProject(id: string) {
    const { data } = await api.patch(`/projects/${id}/close`);
    return data;
}

// ARCHIVE (ACTIVE/CLOSED)
export async function archiveProject(id: string) {
    const { data } = await api.patch(`/projects/${id}/archive`);
    return data;
}

export async function listCompanyPartners(id: string): Promise<CompanyPartner[]> {
    const res = await api.get(`/projects/${id}/company-partners`);
    return res.data.data;
}

export async function listCompanyUsers(id: string): Promise<CompanyUserLite[]> {
    const res = await api.get(`/projects/${id}/company-users`);
    // 응답 형태: { ok: true, data: CompanyUserLite[] } 기준
    return res.data.data;
}

export async function getMembers(id: string): Promise<{ ok: boolean; data: Member[] }> {
    const { data } = await api.get(`/projects/${id}/members`);
    return data;
}

export async function inviteMember(projectId: string, params: { email: string; role: Member['role'] }) {
    // console.log('projectId, email, role : ', projectId, params.email, params.role);
    const { data } = await api.post(`/projects/${projectId}/members`, params);
    return data;
}

export async function updateMemberRole(id: string, userId: string, role: Member['role']) {
    const { data } = await api.patch(`/projects/${id}/members/${userId}`, { role });
    return data;
}

export async function removeMember(id: string, userId: string) {
    const { data } = await api.delete(`/projects/${id}/members/${userId}`);
    return data;
}

export async function listProjectRoles(projectId: string): Promise<Role[]> {
    const { data } = await api.get(`/projects/${projectId}/roles`);
    return data.data;
}

// PM 교체 API
export async function updateProjectPm(projectId: string, payload: { userId?: string; email?: string; name?: string }) {
    const { data } = await api.patch(`/projects/${projectId}/pm`, payload);
    return data;
}

export type ProjectAccess = {
    roleName: string;
    permissions: string[];
    actorProjectCompanyId?: string | null;
    actorProjectCompanyIds?: string[]; // 향후 확장용
    evEntryOrgIds?: string[];
    isOwnerCompanyUser?: boolean;
    isExternalProjectCompanyUser?: boolean;
};

export async function getProjectAccess(projectId: string): Promise<{ ok: boolean; data: ProjectAccess }> {
    const { data } = await api.get(`/projects/${projectId}/access`);
    return data;
}

/**
 * 프로젝트 세팅
 */
export async function getProjectSetting(projectId: string): Promise<{ ok: boolean; data: ProjectSetting }> {
    const { data } = await api.get(`/projects/${projectId}/settings`);
    return data;
}

export async function updateWbsWeekAnchor(
    projectId: string,
    params: { anchorDow: number; applyMode: 'IMMEDIATE' | 'NEXT_WEEK' },
): Promise<{ ok: boolean; data: ProjectSetting }> {
    const { data } = await api.put(`/projects/${projectId}/settings/wbs-week-anchor`, params);
    return data;
}

// ✅ 추가
export async function updateWbsViewPrefs(
    projectId: string,
    params: {
        wbsVisibilityMode?: 'SHOW_ALL_READONLY' | 'HIDE_UNASSIGNED_FOR_COMPANY';
        companyWbsDefaultView?: 'ALL' | 'ONLY_ASSIGNED';
    },
): Promise<{ ok: boolean; data: ProjectSetting }> {
    const { data } = await api.put(`/projects/${projectId}/settings/wbs-view-prefs`, params);
    return data;
}

// ✅ FX Settings
export async function getProjectFxSettings(projectId: string): Promise<{ ok: boolean; data: ProjectFxSetting }> {
    const { data } = await api.get(`/projects/${projectId}/settings/fx`);
    return data;
}

export async function updateProjectFxSettings(
    projectId: string,
    params: {
        anchorCurrency?: string;
        upserts?: Array<{ fromCurrency: string; anchorRate: number; unit?: number; note?: string | null }>;
        deletes?: Array<{ fromCurrency: string }>;
    },
): Promise<{ ok: boolean; data: ProjectFxSetting }> {
    const { data } = await api.put(`/projects/${projectId}/settings/fx`, params);
    return data;
}

export async function lockProjectFxSettings(projectId: string): Promise<{ ok: boolean; data: ProjectFxSetting }> {
    const { data } = await api.post(`/projects/${projectId}/settings/fx/lock`);
    return data;
}

export async function updateWbsEvApprovalPolicy(
    projectId: string,
    params: { wbsEvApproveBeforeClose: boolean },
): Promise<{ ok: boolean; data: ProjectSetting }> {
    const { data } = await api.put(`/projects/${projectId}/settings/wbs-ev-approval-policy`, params);
    return data;
}
